import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";

const ISSUE_OPTIONS = [
  { value: "spelling_error", title: "Spelling / grammar", description: "Typos or awkward wording that changes meaning." },
  { value: "question_text_error", title: "Question text is wrong", description: "Missing info, unclear prompt, or incorrect statement." },
  { value: "options_error", title: "Options are wrong", description: "Duplicate options, wrong choices, or missing correct option." },
  { value: "incorrect_image", title: "Incorrect image", description: "Image doesn’t match the question or is missing/unclear." },
  { value: "incorrect_answer", title: "Incorrect answer", description: "The marked correct answer is not correct." },
  { value: "incorrect_explanation", title: "Incorrect explanation", description: "Explanation doesn’t match the answer or concept." },
  { value: "formatting_issue", title: "Formatting issue", description: "Hard to read, broken layout, symbols, spacing, etc." },
  { value: "other", title: "Other", description: "Something else not listed above." },
];

const QuestionReportButton = ({ questionId, studentId, teacherId, variant = "pill" }) => {
  const [reportOpen, setReportOpen] = useState(false);
  const [reportIssueType, setReportIssueType] = useState("");
  const [reportDescription, setReportDescription] = useState("");
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    setReportOpen(false);
    setReportIssueType("");
    setReportDescription("");
    setReportSubmitting(false);
  }, [questionId]);

  // Lock background scroll when modal is open
  useEffect(() => {
    if (!reportOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, [reportOpen]);

  const showToast = (type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 2600);
  };

  const handleSubmitReport = async () => {
    if (!reportIssueType) return;

    // must have exactly one reporter (matches your model validation)
    if (!studentId && !teacherId) {
      showToast("error", "Please sign in again, then try reporting.");
      return;
    }

    try {
      setReportSubmitting(true);

      await axios.post(`${API_BASE_URL}/api/create/question_report`, {
        question_id: questionId,
        student_id: studentId || null,
        teacher_id: teacherId || null,
        issue_type: reportIssueType,
        description: reportDescription.trim() || null,
      });

      setReportOpen(false);
      setReportIssueType("");
      setReportDescription("");
      showToast("success", "Thanks! Your report was submitted.");
    } catch (err) {
      console.error("Report submit failed:", err);
      showToast("error", "Couldn’t submit report. Please try again.");
    } finally {
      setReportSubmitting(false);
    }
  };

  const handleClose = () => {
    if (reportSubmitting) return;
    setReportOpen(false);
  };

  return (
    <>
    <button
      type="button"
      onClick={() => setReportOpen(true)}
      className={
        variant === "link"
          ? "shrink-0 text-xs font-semibold text-[#1896b9] hover:text-[#064759] flex items-center gap-1"
          : "shrink-0 text-xs font-semibold text-gray-700 hover:text-gray-900 border border-gray-200 hover:border-gray-300 rounded-full px-3 py-1.5 bg-white hover:bg-gray-50 transition flex items-center gap-2"
      }
    >
      <span className={variant === "link" ? "text-xs" : "text-sm"}>⚑</span>
      Report
    </button>

      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[60]">
          <div
            className={`rounded-full px-4 py-2 text-xs shadow-sm border ${
              toast.type === "success"
                ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                : "bg-rose-50 text-rose-800 border-rose-200"
            }`}
          >
            {toast.message}
          </div>
        </div>
      )}

      {reportOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
          role="dialog"
          aria-modal="true"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/30"
            onClick={handleClose}
          />

          {/* Panel
              - Mobile: full height (scroll inside)
              - Desktop: centered card with max height
          */}
          <div
            className="
              relative w-full
              h-[100dvh] sm:h-auto
              sm:max-w-lg sm:max-h-[85vh]
              bg-white
              sm:rounded-2xl rounded-none
              shadow-xl border border-gray-200
              overflow-hidden
              flex flex-col
            "
          >
            {/* Header (always visible) */}
            <div className="shrink-0 p-5 border-b border-gray-100 bg-white">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Report a problem</h3>
                  <p className="text-xs text-gray-600 mt-1">
                    Reporting helps keep the question bank accurate. This does{" "}
                    <span className="font-semibold">not</span> affect your score or mastery.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleClose}
                  className="rounded-full w-8 h-8 flex items-center justify-center hover:bg-gray-50 border border-gray-200 text-gray-600"
                  aria-label="Close report modal"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Body (scrollable) */}
            <div className="flex-1 min-h-0 overflow-y-auto p-5 overscroll-contain">
              <p className="text-xs font-semibold text-gray-800 mb-2">
                What’s wrong with this question?
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {ISSUE_OPTIONS.map((opt) => {
                  const selected = reportIssueType === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setReportIssueType(opt.value)}
                      className={`text-left rounded-xl border p-3 transition ${
                        selected
                          ? "border-sky-300 bg-sky-50"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <p className="text-xs font-semibold text-gray-900">{opt.title}</p>
                      <p className="text-[11px] text-gray-600 mt-1">{opt.description}</p>
                    </button>
                  );
                })}
              </div>

              <div className="mt-4">
                <label className="block text-xs font-semibold text-gray-800 mb-2">
                  Optional notes
                </label>
                <textarea
                  value={reportDescription}
                  onChange={(e) => setReportDescription(e.target.value)}
                  rows={4}
                  placeholder="Example: The explanation says X, but the correct concept is Y."
                  className="w-full rounded-xl border border-gray-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
                />
                <p className="text-[11px] text-gray-500 mt-2">
                  Tip: If you can, mention what you think the correct fix should be.
                </p>
              </div>
            </div>

            {/* Footer (always visible) */}
            <div className="shrink-0 p-5 border-t border-gray-100 bg-white flex items-center justify-between gap-3">
              <p className="text-[11px] text-gray-500">
                Question ID: <span className="font-mono text-gray-700">{questionId}</span>
              </p>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={reportSubmitting}
                  className="rounded-xl border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleSubmitReport}
                  disabled={!reportIssueType || reportSubmitting}
                  className="rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60"
                >
                  {reportSubmitting ? "Submitting..." : "Submit report"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QuestionReportButton;
